import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { isEmpty } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import useStayScrolled from 'react-stay-scrolled';
import { CustomUser } from 'api';
import { authAction, firmsAction } from 'common/actions';
import { ERROR_403 } from 'common/config/api';
import { firmSummaryUrl } from 'common/config/urls';
import { FUND_PERMISSION_NAME } from 'common/constants/sidebar';
import { COMPANY_NAME_TITLE } from 'common/constants/summary';
import { useStore } from 'common/store';
import { Firm } from 'common/types/firm/firm';
import { UseStoreValues } from 'common/types/store';
import { HiddenContentChip } from 'components';
import { dashboardCompanyOverviewUrl, WORKBOARD_CURRENT_VALUATIONS_URL } from 'dashboard409a/common/config/urls';
import { getPreviousWorkboardRoute } from 'dashboard409a/utilities/navigation';
import { FirmStepper } from 'layouts/Main/components';
import { TermsAgreed } from 'pages/SignUp/components';
import { useGetCompanyInfoById } from 'services/hooks/company';
import { useGetFundById } from 'services/hooks/fund';
import { useUpdateUserProfile } from 'services/hooks/useUserSelected';
import { confirmNoValue, dbShortDate, isFalseStrict } from 'utilities';
import { clearPreviousPortfolioRoute, getPreviousPortfolioRoute } from 'utilities/appSwitch';
import CompanySelector from '../CompanySelector';
import FirmList from '../FirmList';
import ListHeader from '../ListHeader';
import LoadingPaper from '../LoadingPaper';
import Otp from '../OTP';
import { OrganizationSelectorProps, Status2FA } from './OrganizationSelector.props';
import { FirmListContainer, RootPaper } from './OrganizationSelector.styles';
import { LoginFirm } from '../FirmList/FirmList.props';

const FILTER_FIRM_NUMBER = 9;

const OrganizationSelector = ({ lowerPermissions = [], isDashboard = false }: OrganizationSelectorProps) => {
  const { mutate: updateUserProfile } = useUpdateUserProfile();

  const divRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const getCompanyInfo = useGetCompanyInfoById();
  const [, getFundInfo] = useGetFundById();
  const [{ firmList, firmListAuthorized, user }, dispatch] = useStore() as unknown as UseStoreValues;
  const [search, setSearch] = useState<string>('');
  const [hiddenBottom, setHiddenBottom] = useState<number>(0);
  const [openStepper, setOpenStepper] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [status2fa, setStatus2fa] = useState<Status2FA>({ requires_2fa: null, user_has_2fa: null });
  const [otpVerified, setOtpVerified] = useState<boolean>(false);
  const [needsToAcceptTerms, setNeedsToAcceptTerms] = useState<boolean>(false);
  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
  const inputFilterRef = useRef<HTMLInputElement>(null);

  useStayScrolled(divRef);

  const hasNotFirmPermissions = useMemo(
    () => firmList?.every(firm => firm.user_permissions?.length === 0) && user && !user.is_superuser,
    [firmList, user]
  );

  // Check if user needs to accept the terms and permissions (only for new users)
  useEffect(() => {
    if (user) {
      setNeedsToAcceptTerms(!user?.profile?.terms_last_agreed);
    }
  }, [user]);

  const handleAcceptTerms = useCallback(() => {
    const now = dbShortDate(new Date(), 'YYYY-MM-DD HH:mm:ss');
    updateUserProfile(
      {
        userId: user?.id ?? 0,
        updatedUserProfile: {
          username: user?.username ?? '',
          email: user?.email ?? '',
          profile: {
            terms_last_agreed: now,
          },
        },
      },
      {
        onSuccess: () => {
          setNeedsToAcceptTerms(false);
          dispatch(
            authAction.setUserInfo({
              ...user,
              profile: {
                ...user?.profile,
                terms_last_agreed: now,
              },
            })
          );
        },
      }
    );
  }, [user, updateUserProfile, dispatch]);

  const addFirmHandler = useCallback(() => setOpenStepper(true), [setOpenStepper]);
  const onFirmStepperClose = useCallback(() => setOpenStepper(false), [setOpenStepper]);

  const getHiddenFirms = () => {
    const firmListContainer = document.querySelector('#firm-list') as HTMLElement;
    const firmListItems = document.querySelectorAll('#firm-list > a');

    let tmpHiddenBottom = 0;

    if (!firmListContainer) {
      return;
    }

    firmListItems.forEach(firm => {
      const htmlFirm = firm as HTMLElement;
      if (htmlFirm.offsetTop >= firmListContainer.offsetHeight + firmListContainer.scrollTop) {
        tmpHiddenBottom += 1;
      }
    });

    setHiddenBottom(tmpHiddenBottom);
  };

  const is2FANotRequired = useCallback(() =>
    isFalseStrict(status2fa.requires_2fa) && isFalseStrict(status2fa.user_has_2fa)
  , [status2fa]);

  const handleLowerPermissions = () => {
    let featureObject;
    let objectPath;
    /* for restricted user, the only scenario where it has multiple permissions is when it has
    permissions for a single fund, and the companies in which the fund invested */
    const fundPermission = lowerPermissions?.find(
      ({ feature_object }) => feature_object?.object_type === FUND_PERMISSION_NAME
    );

    if (lowerPermissions.length > 1 && fundPermission) {
      objectPath = fundPermission.feature_object_path;
      featureObject = fundPermission.feature_object;
    } else {
      featureObject = lowerPermissions[0].feature_object;
      objectPath = lowerPermissions[0].feature_object_path;
    }
    /* need to set some global state in store to say this user is restricted to only a company
    and then in next component use that global state to call getActiveCompanyExtendedInfo */
    dispatch(authAction.setIsRestrictedUser(true));
    // some store info needs to be set to have working containers
    if (featureObject?.object_type === COMPANY_NAME_TITLE) {
      getCompanyInfo(featureObject.object_id);
    } else if (featureObject?.object_id) {
      getFundInfo(featureObject.object_id);
    }
    return navigate(`/${objectPath}`);
  };

  const fetchFirmList = useCallback(async () => {
    try {
      setIsLoading(true);
      const { firms, requires_2fa, user_has_2fa } = await firmsAction.getFirmList();
      if (firms === ERROR_403) {
        dispatch(firmsAction.setFirmList(null));
        dispatch(firmsAction.setFirmListAuthorized(false));
      } else {
        dispatch(firmsAction.setFirmList(firms));
        dispatch(firmsAction.setFirmListAuthorized(true));
      }
      setStatus2fa({ requires_2fa, user_has_2fa });
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      throw new Error(error as string);
    }
  }, [dispatch]);

  useEffect(() => {
    if (isEmpty(firmList)) {
      fetchFirmList();
    } else {
      setIsLoading(false);
    }
  // We need to fetch the firm list on mount only, otherwise if I'm a new user it will fetch the firm list infinitely 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (inputFilterRef.current) {
        inputFilterRef.current.focus();
      }
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const handleDeepLinkRedirect = useCallback(() => {
    const hasDeepLink = location.state?.from?.pathname && location.state?.from?.search;
    if (hasDeepLink && !needsToAcceptTerms && is2FANotRequired()) {
      navigate(`${location.state.from.pathname}${location.state.from.search}`);
      return true;
    }
    return false;
  }, [location.state, needsToAcceptTerms, is2FANotRequired, navigate]);

  const handleFirmRedirect = useCallback(() => {
    const canRedirectToFirm = !isDashboard && !isEmpty(firmList) && !firmListAuthorized && !hasNotFirmPermissions;
    const shouldRedirect = firmList?.length === 1 && !needsToAcceptTerms && history && is2FANotRequired();

    if (canRedirectToFirm && shouldRedirect) {
      navigate(firmSummaryUrl(firmList?.[0]?.slug ?? ''));
      return true;
    }
    return false;
  }, [isDashboard, firmList, firmListAuthorized, hasNotFirmPermissions, needsToAcceptTerms, is2FANotRequired, navigate]);

  const handleCompanyRedirect = useCallback(() => {
    const hasCompanies = user?.standalone_companies_permissions && user.standalone_companies_permissions.length > 0;
    const shouldRedirect = user?.standalone_companies_permissions?.length === 1 && history && is2FANotRequired();

    if (hasCompanies && shouldRedirect) {
      navigate(dashboardCompanyOverviewUrl(user.standalone_companies_permissions[0].slug ?? ''));
      return true;
    }
    return false;
  }, [user, is2FANotRequired, navigate]);

  const handlePreviousPortfolioRedirect = useCallback(() => {
    if (!isDashboard && otpVerified) {
      const savedRoute = getPreviousPortfolioRoute(user?.id);
      if (savedRoute) {
        clearPreviousPortfolioRoute(user?.id);
        navigate(savedRoute);
        return true;
      }
    }
    return false;
  }, [isDashboard, otpVerified, user, navigate]);

  const handleStaffRedirect = useCallback(() => {
    // Only redirect staff to workboard if isDashboard is true (Company Valuations flow)
    // If isDashboard is false, user chose Portfolio Valuation and should see firm list
    if (isDashboard && user?.is_staff && is2FANotRequired()) {
      const savedWorkboardRoute = getPreviousWorkboardRoute();
      navigate(savedWorkboardRoute || WORKBOARD_CURRENT_VALUATIONS_URL);
      return true;
    }
    return false;
  }, [isDashboard, user, is2FANotRequired, navigate]);

  useEffect(() => {
    if (handleDeepLinkRedirect()) return;
    if (handlePreviousPortfolioRedirect()) return;
    if (handleFirmRedirect()) return;
    if (handleCompanyRedirect()) return;
    if (handleStaffRedirect()) return;

    if (hasNotFirmPermissions && lowerPermissions.length) {
      handleLowerPermissions();
    }

    getHiddenFirms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmList, firmListAuthorized, isLoading, lowerPermissions, otpVerified]);

  if ((isDashboard || isEmpty(firmList)) && !isLoading && (user?.standalone_companies_permissions ?? []).length > 0) {
    return <CompanySelector />;
  }

  if (user && !confirmNoValue(status2fa.user_has_2fa) && !otpVerified) {
    return <Otp userHas2FA={status2fa.user_has_2fa} verifyOtp={setOtpVerified} />;
  }

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearch(value || '');
  };

  return (
    <>
      {isLoading ? (
        <>
          <LoadingPaper message="Loading..." />
          <div ref={divRef} />
        </>
      ) : (
        <>
          {user && !otpVerified && (
            <Otp
              userHas2FA={(status2fa.user_has_2fa as unknown as boolean) || (user?.has_2fa as unknown as boolean)}
              verifyOtp={setOtpVerified}
            />
          )}
          {needsToAcceptTerms && otpVerified && (
            <>
              <TermsAgreed termsAgreed={acceptTerms} setTermsAgreed={setAcceptTerms} />
              <Button onClick={handleAcceptTerms} variant="contained" color="primary" disabled={!acceptTerms}>
                Accept Terms
              </Button>
            </>
          )}
          {!needsToAcceptTerms && otpVerified && (
            <RootPaper>
              <ListHeader
                onInputChange={onChange}
                searchState={search}
                firmList={firmList as Firm[]}
                inputFilterRef={inputFilterRef}
                listLimit={FILTER_FIRM_NUMBER}
              />
              <FirmListContainer id="firm-list" component="nav" ref={divRef} onScroll={getHiddenFirms}>
                <FirmList
                  firmList={firmList as LoginFirm[]}
                  firmListAuthorized={firmListAuthorized}
                  addFirmHandler={addFirmHandler}
                  searchState={search}
                  user={user as CustomUser}
                />
                {openStepper && <FirmStepper isVisible={openStepper} onClose={onFirmStepperClose} />}
              </FirmListContainer>
              <HiddenContentChip
                id="hidden-firms"
                isVisible={hiddenBottom > 0}
                label={`${hiddenBottom} firms`}
                position="bottom"
              />
            </RootPaper>
          )}
        </>
      )}
    </>
  );
};

export default OrganizationSelector;
